import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookMarked, ChevronDown, ExternalLink, Search } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CommentaryAccessNotice, isAccessError } from "./CommentaryAccess";
import { CommentaryNoNotes } from "./CommentaryNoNotes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CommentarySection } from "./CommentarySection";

export const COMMENTARY_KEY = "qg-commentary-slug";

export function useCommentaries() {
  const { user, loading } = useAuth();
  return useQuery({ queryKey: ["commentaries", user?.user_id || "anon"], queryFn: async () => (await api.get("/commentaries")).data.commentaries, staleTime: 60 * 60 * 1000, enabled: !loading });
}

/* Keeps the chosen slug valid: when the viewer can't see it (hidden / logged out), fall back to the first visible one. */
export function useValidCommentarySlug(slug, setSlug) {
  const { data: list } = useCommentaries();
  useEffect(() => {
    if (list && list.length && !list.some((c) => c.slug === slug)) setSlug(list[0].slug);
  }, [list, slug, setSlug]);
  return list;
}

export function useCommentaryChapter(slug, book, chapter, verse) {
  return useQuery({
    queryKey: ["commentary", slug, book, Number(chapter), verse ?? null],
    queryFn: async () => (await api.get(`/commentaries/${slug}/${encodeURIComponent(book)}/${chapter}`, { params: verse ? { verse } : {} })).data,
    enabled: !!slug && !!book && !!chapter,
    retry: (count, err) => !isAccessError(err) && count < 2,
    staleTime: 10 * 60 * 1000,
  });
}

export function CommentaryPicker({ slug, onChange, testId = "commentary-picker" }) {
  const { data: list } = useCommentaries();
  if (!list || list.length < 2) return <span className="font-serif text-base font-semibold text-foreground" data-testid={testId}>{list?.[0]?.name || "Commentary"}</span>;
  return (
    <Select value={slug} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-[170px] rounded-full bg-background font-serif text-[15px]" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{list.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function ChapterIntro({ paragraphs, label = "Chapter overview", defaultOpen = false, testId = "commentary-intro" }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!paragraphs?.length) return null;
  return (
    <div className="rounded-2xl border border-dashed border-border p-4" data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-2 text-left" data-testid={`${testId}-toggle`}>
        <span className="font-mono text-[11px] uppercase tracking-wider text-gold">{label}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-3 space-y-3 font-serif text-[15px] leading-relaxed text-foreground/85">{paragraphs.map((p, i) => <p key={`${i}-${p.length}`}>{p}</p>)}</div>}
    </div>
  );
}

/* Side panel for the Bible Reader: the chosen commentary for the current chapter, synced to the active verse. */
export default function CommentaryPanel({ slug, onSlug, book, chapter, verse, onJump, actions, highlights }) {
  const { data, isLoading, isError, error } = useCommentaryChapter(slug, book, chapter);
  const navigate = useNavigate();
  const list = useValidCommentarySlug(slug, onSlug);
  const current = list?.find((c) => c.slug === slug);
  const name = current?.name || "Commentary";
  const scrollRef = useRef(null);
  const activeId = useMemo(() => data?.sections.find((s) => verse && s.verse_start <= verse && s.verse_end >= verse)?.id, [data, verse]);
  useEffect(() => {
    if (!activeId) return undefined;
    const go = () => {
      const box = scrollRef.current;
      // scoped lookup: on phones a hidden desktop copy of this panel is also mounted
      const el = box?.querySelector(`[id="${CSS.escape(activeId)}"]`)?.closest("[data-hl-scope]");
      if (!el || !box) return;
      if (box.scrollHeight > box.clientHeight + 4) box.scrollTo({ top: el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - 12, behavior: "smooth" });
      else el.scrollIntoView({ block: "start", behavior: "smooth" });
    };
    // second pass: inside the mobile sheet the panel is still sliding in on the first one
    const timers = [setTimeout(go, 150), setTimeout(go, 650)];
    return () => timers.forEach(clearTimeout);
  }, [activeId]);
  useEffect(() => { if (!verse) scrollRef.current?.scrollTo({ top: 0 }); }, [book, chapter, verse]);

  return (
    <aside className="flex h-full flex-col rounded-2xl border border-border bg-card" data-testid="commentary-panel" data-lenis-prevent>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-3 sm:px-4">
        <div className="flex min-w-0 items-center gap-2">
          <BookMarked className="hidden h-4 w-4 text-gold sm:block" />
          <CommentaryPicker slug={slug} onChange={onSlug} />
          <span className="hidden font-mono text-[10px] uppercase tracking-wider text-muted-foreground md:inline">on {book} {chapter}</span>
        </div>
        <div className="flex items-center gap-1">
          <Link to={`/commentary/${slug}/search`} title="Search this commentary" className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="commentary-panel-search"><Search className="h-4 w-4" /></Link>
          <Link to={`/commentary/${slug}/${encodeURIComponent(book)}/${chapter}`} title="Open full commentary page" className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="commentary-panel-open"><ExternalLink className="h-4 w-4" /></Link>
        </div>
        {current && <p className="w-full truncate font-serif text-sm italic text-gold" title={current.display_name} data-testid="commentary-panel-full-name">{current.display_name}</p>}
      </header>
      <div ref={scrollRef} className="relative min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3 [scrollbar-width:thin]">
        {isLoading && <div className="space-y-3 p-3">{Array.from({ length: 6 }).map((_, i) => <div key={`skeleton-${i}`} className="h-5 animate-pulse rounded bg-muted" />)}</div>}
        {isError && (isAccessError(error) ? <div className="p-3"><CommentaryAccessNotice error={error} compact /></div> : <p className="p-4 text-sm text-muted-foreground" data-testid="commentary-panel-empty">Something went wrong loading {name} on {book} {chapter}.</p>)}
        {data && !data.has_notes && <CommentaryNoNotes name={name} book={book} chapter={chapter} prev={data.prev} next={data.next} compact onGo={(t) => navigate(`/commentary/${slug}/${encodeURIComponent(t.book)}/${t.chapter}`)} />}
        {data?.has_notes && <ChapterIntro paragraphs={data.intro} />}
        {data?.sections.map((s) => (
          <CommentarySection key={s.id} s={s} slug={slug} name={name} actions={actions} highlights={highlights.filter((h) => h.section_id === s.id)} active={s.id === activeId} onJump={onJump} compact />
        ))}
      </div>
      <footer className="border-t border-border px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        Select any text to highlight it or send it to your journal
      </footer>
    </aside>
  );
}
